<template>
    <LoadingModal
        v-if="loading"
        @close="$emit('abort')"
    />
    <SetRemindersModal
        v-else
        open
        :type="type"
        :initial-value="initialValue"
        :submitting="submitting"
        @close="$emit('abort')"
        @submit="onSubmit"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { NotificationName } from '@evidentid/gateway-api-client/src/models';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';
    import {
        NotificationCadence,
        NotificationsConfigurationState,
    } from '@/modules/notifications-configuration/types';

    import SetRemindersModal from '../components/SetRemindersModal.vue';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            LoadingModal,
            SetRemindersModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class SetRemindersProcedure extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: String })
        private type!: NotificationName;

        private get state(): NotificationsConfigurationState {
            return this.notificationsConfigurationStore.$state;
        }

        private get initialValue(): any | null {
            return this.state.cadences?.[this.rpName]?.[this.type] || null;
        }

        private get loadStatus(): OperationStatus {
            return this.state.loadCadences?.[this.rpName]?.[this.type] || OperationStatus.uninitialized;
        }

        private get updateStatus(): OperationStatus {
            return this.state.updateCadences?.[this.rpName]?.[this.type] || OperationStatus.uninitialized;
        }

        private get loading(): boolean {
            return [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.loadStatus);
        }

        private get submitting(): boolean {
            return this.updateStatus === OperationStatus.loading;
        }

        private async onSubmit(cadence: NotificationCadence): Promise<void> {
            await this.notificationsConfigurationStore.updateNotificationCadence({
                rpName: this.rpName,
                type: this.type,
                cadence,
            });
            if (this.updateStatus === OperationStatus.success) {
                await this.notificationsConfigurationStore.loadEmailTemplates({ rpName: this.rpName });
                await useSnackbarStore().displaySnackbar({
                    message: 'Updated reminders successfully.',
                    success: true,
                });
                this.$emit('finish');
            }
        }

        private mounted(): void {
            this.notificationsConfigurationStore.loadNotificationCadence({
                rpName: this.rpName,
                type: this.type,
            });
        }
    }
</script>
